import React from 'react'
import { PGA_COM_HOST } from 'env'
import PropTypes from 'prop-types'
import { isEmpty, get } from 'lodash'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import Avatar from '@material-ui/core/Avatar'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import ExternalLinkButton from 'components/external-link-button'
import RouterLinkButton from 'components/router-link-button'
import IconFlag from 'components/icons/icon-flag'
import IconMarker from 'components/icons/icon-marker'
import { formatNameForDisplay } from 'lib/text-display'
import ToggleVisibility from './ToggleVisibility'
import { secureLink } from 'lib/utils'
import { useStyles } from './styles'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import { useIsMobile } from 'lib/hooks'
import QRCode from 'react-qr-code'

const getFacilityName = facilities => get(facilities, ['0', 'facilityName'], '')

const getFacilityAddress = facilities => {
  const city = get(facilities, ['0', 'city'])
  const state = get(facilities, ['0', 'state'])
  if (city && state) {
    return `${city}, ${state}`
  }
  return ''
}

const CoachProfileCard = ({ coachProfile, toggleCoachStatus }) => {
  const {
    coachProfileId,
    firstName,
    lastName,
    facilities,
    profilePhoto,
    title,
    status,
    customSlug,
    city,
    state,
    coach,
  } = coachProfile

  const classes = useStyles()
  const facilityAddress = getFacilityAddress(facilities)
  const facilityName = getFacilityName(facilities)
  const coachName = `${firstName} ${lastName}`
  const [isQRcodeEnabled] = useFeatureFlags([flags.FLAG_FEAT_COACH_QR_CODE])
  const isMobile = useIsMobile()
  const hasActiveCoachProfile = coachProfile?.status === 'ACTIVE'
  const coachProfileUrl = `${PGA_COM_HOST}/coach/${customSlug}?from-qr-code=true`

  return (
    <>
      <Card variant="outlined" className={classes.cardRoot}>
        <Box className={classes.avatarWrapper}>
          <Avatar
            aria-label="coach"
            src={secureLink(profilePhoto)}
            className={classes.avatarStyles}
          >
            {!profilePhoto &&
              `${firstName.substring(0, 1)}${lastName.substring(0, 1)}`}
          </Avatar>
        </Box>
        {coach.canPromoteContent && (
          <ToggleVisibility
            coachProfileId={coachProfileId}
            status={status}
            toggleCoachStatus={toggleCoachStatus}
          />
        )}
        <CardContent className={classes.cardContent}>
          <Grid container>
            <Grid item xs={12} sm={6} container direction="column">
              <Typography variant="h4">
                {formatNameForDisplay(coachName)}
              </Typography>
              <Typography variant="body2" className={classes.titleStyles}>
                {title}
              </Typography>
              {!isEmpty(city) && !isEmpty(state) && (
                <div className={classes.iconWrapper}>
                  <span className={classes.cardDetailIcon}>
                    <IconMarker />
                  </span>
                  <span className={classes.cardDetail}>
                    {city}, {state}
                  </span>
                </div>
              )}
              {!isEmpty(facilityName) && !isEmpty(facilityAddress) && (
                <div className={classes.iconWrapper}>
                  <span className={classes.cardDetailIcon}>
                    <IconFlag />
                  </span>
                  <span className={classes.cardDetail}>
                    {facilityName}, {facilityAddress}
                  </span>
                </div>
              )}
            </Grid>
            {isQRcodeEnabled && hasActiveCoachProfile && !isMobile && (
              <Grid
                container
                item
                xs={false}
                sm={6}
                direction="column"
                justifyContent="center"
                alignItems="center"
              >
                <QRCode
                  value={coachProfileUrl}
                  data-testid="coach-profile-qr-code"
                  size={100}
                  level="H" // error correction level (H = high) for more squareness and less likely to be misread
                  style={{
                    border: '1px solid #AB9157',
                    borderRadius: '4px',
                    padding: '10px',
                  }}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
        <CardActions className={classes.cardActions}>
          <div className={classes.adminButtons}>
            <ExternalLinkButton href={`${PGA_COM_HOST}/coach/${customSlug}`}>
              View
            </ExternalLinkButton>
            <RouterLinkButton to="profile/edit">Edit</RouterLinkButton>
          </div>
        </CardActions>
      </Card>
    </>
  )
}

CoachProfileCard.propTypes = {
  coachprofile: PropTypes.object,
  adminPage: PropTypes.bool,
  toggleCoachStatus: PropTypes.func,
}

export default CoachProfileCard
